/* oxlint-disable no-console */
import { TMessageListFileType } from '@cian/chats-api';
import { Checkbox, Responsive, UIText1, UIText2 } from '@cian/ui-kit';
import { IconToggleStarOn12 } from '@cian/ui-kit-design-tokens/icons';
import classnames from 'clsx';
import * as React from 'react';

import * as styles from './ChatListItem.css';
import { ChatListItemSwipeButton, EChatListItemSwipeButtonType, LastMessageContent } from './components';

import { ERequestApiStatus } from '../../types/appState';
import { TAvailableActions } from '../../types/availableActions';
import { TOutcomingStatus } from '../../types/messages';
import { ERoute, TRoute } from '../../types/route';
import { useLeftSwipeVisible } from '../../utils/useLeftSwipeVisible';
import { MessageStatusIcon } from '../MessageStatusIcon';

type TChatListItemProps = {
  chatId: string;
  checkboxChecked: boolean;
  checkboxShown: boolean;
  isActive: boolean;
  fileLastMessage?: { type: TMessageListFileType; name: string };
  isOfferLastMessage?: boolean;
  isDeletedLastMessage?: boolean;
  message: string;
  isMultilineMessage: boolean;
  onClick(): void;
  onSwipeButtonClick(action: EChatListItemSwipeButtonType): void;
  status?: TOutcomingStatus;
  unreadCount: number;
  date: string;
  previewNode: React.ReactNode;
  currentRoute: TRoute;
  lastMessageName?: string;
  openedChatRef?: React.RefObject<HTMLDivElement | null>;
  isStarred: boolean;
  availableActions?: TAvailableActions[];
  chatListStatus: ERequestApiStatus;
  title: React.ReactNode;
  chatInfo?: string | null;
};

const ChatListItemСomponent: React.FC<TChatListItemProps> = ({
  chatId,
  checkboxChecked,
  checkboxShown,
  isActive,
  fileLastMessage,
  message,
  isMultilineMessage,
  isOfferLastMessage,
  isDeletedLastMessage,
  onClick,
  onSwipeButtonClick,
  status,
  unreadCount,
  date,
  title,
  chatInfo,
  previewNode,
  currentRoute,
  lastMessageName,
  openedChatRef,
  isStarred,
  availableActions,
  chatListStatus,
}) => {
  const { isLeftSwipeVisible } = useLeftSwipeVisible();
  const itemContainerRef = React.useRef<HTMLDivElement | null>(null);
  const [isItemDisplay, setIsItemDisplay] = React.useState(false);
  const [isItemVisible, setIsItemVisible] = React.useState(false);

  const isMarkAsStarredAvailable = availableActions?.includes('markAsStarred');
  const isUnmarkAsStarredAvailable = availableActions?.includes('unmarkAsStarred');

  const scrollItemToCenter = () => {
    setTimeout(() => {
      if (itemContainerRef.current) {
        itemContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      }
    }, 100);
  };

  const onSwipeButtonClickCallback = (type: EChatListItemSwipeButtonType) => {
    onSwipeButtonClick(type);
    scrollItemToCenter();
  };

  React.useEffect(() => {
    if (chatListStatus === ERequestApiStatus.Succeed) {
      setIsItemDisplay(true);
      setTimeout(() => {
        setIsItemVisible(true);
      }, 300);
    }
    if (chatListStatus === ERequestApiStatus.Pending) {
      setIsItemVisible(false);
      setIsItemDisplay(false);
    }
  }, [chatListStatus]);

  return (
    <div
      className={classnames(
        styles['item'],
        isActive && styles['item--active'],
        isItemDisplay ? styles['item-swipe--display'] : styles['item-swipe--no-display'],
        isItemVisible ? styles['item-swipe--visible'] : styles['item-swipe--hidden'],
      )}
      onClick={onClick}
      data-chatid={chatId}
      ref={isActive ? openedChatRef : null}
      data-name="ChatListItem"
    >
      {!checkboxShown && (
        <Responsive phone tablet>
          <div
            className={classnames(
              styles['left-swipe-button'],
              /* TODO: функционал непрочитанности пока не готов на сервере, а прочитать можно */
              unreadCount > 0 && isLeftSwipeVisible && styles['left-swipe-button--shown'],
            )}
          >
            <ChatListItemSwipeButton
              type={unreadCount > 0 ? EChatListItemSwipeButtonType.Read : EChatListItemSwipeButtonType.Unread}
              onClick={onSwipeButtonClickCallback}
            />
          </div>
        </Responsive>
      )}

      <div className={styles['item-container']} ref={itemContainerRef}>
        {checkboxShown && (
          <span className={styles['checkbox']}>
            <Checkbox onClick={e => e.preventDefault()} value={chatId} checked={checkboxChecked} />
          </span>
        )}
        <div className={styles['preview-container']}>
          {previewNode}
          {isStarred && currentRoute !== ERoute.Starred && (
            <div className={styles['starred-badge']}>
              <IconToggleStarOn12 color={'icon-main-default'} />
            </div>
          )}
        </div>
        <div className={styles['info']}>
          <div className={styles['common']}>
            <span className={styles['username']}>{title}</span>
            <UIText2 color="text-secondary-default" whiteSpace="nowrap">
              {date}
            </UIText2>
            {status && <MessageStatusIcon status={status} />}
          </div>
          <div className={styles['details']}>
            <div className={styles['texts']}>
              {chatInfo && (
                <UIText1>
                  <div className={styles['chat-info']} data-testid="chat_list_item_chat_info">
                    {chatInfo}
                  </div>
                </UIText1>
              )}
              <UIText1 color="text-secondary-default">
                <LastMessageContent
                  name={lastMessageName}
                  message={message}
                  multiline={isMultilineMessage}
                  file={fileLastMessage}
                  isOffer={isOfferLastMessage}
                  deleted={isDeletedLastMessage}
                />
              </UIText1>
            </div>
            {unreadCount !== 0 && (
              <span data-testid="unreadCounter" className={styles['counter']}>
                {unreadCount}
              </span>
            )}
          </div>
        </div>
      </div>
      {!checkboxShown && (
        <Responsive phone tablet>
          {currentRoute === ERoute.Spam ? (
            <div className={classnames(styles['right-swipe-button'])}>
              <ChatListItemSwipeButton
                type={EChatListItemSwipeButtonType.Unblock}
                onClick={onSwipeButtonClickCallback}
              />
            </div>
          ) : (
            <>
              <div className={classnames(styles['right-swipe-button'])}>
                <ChatListItemSwipeButton
                  type={EChatListItemSwipeButtonType.Delete}
                  onClick={onSwipeButtonClickCallback}
                />
              </div>
              <div className={classnames(styles['right-swipe-button'])}>
                <ChatListItemSwipeButton
                  type={
                    currentRoute === ERoute.Archive
                      ? EChatListItemSwipeButtonType.Return
                      : EChatListItemSwipeButtonType.Archive
                  }
                  onClick={onSwipeButtonClickCallback}
                />
              </div>
            </>
          )}
          {isStarred && isUnmarkAsStarredAvailable && (
            <div className={classnames(styles['right-swipe-button'])}>
              <ChatListItemSwipeButton
                type={EChatListItemSwipeButtonType.Unstarred}
                onClick={onSwipeButtonClickCallback}
              />
            </div>
          )}
          {!isStarred && isMarkAsStarredAvailable && (
            <div className={classnames(styles['right-swipe-button'])}>
              <ChatListItemSwipeButton
                type={EChatListItemSwipeButtonType.Starred}
                onClick={onSwipeButtonClickCallback}
              />
            </div>
          )}
        </Responsive>
      )}
    </div>
  );
};

export const ChatListItem = React.memo(ChatListItemСomponent);
